import React, { useState, useRef, useEffect, useCallback } from "react";
import { buildSystemPrompt } from "./chatbotKnowledge";
import ChatbotGalleryStrip from "./ChatbotGalleryStrip";
import ghassenImg from "../../assets/images/ghassen_image.png";
import "./Chatbot.css";

// Lightweight markdown renderer - handles bold, italic, inline code, bullet/numbered lists
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let key = 0;

  // Inline parser: bold, italic (not adjacent to *), inline code
  // Uses negative lookahead/lookbehind so italic \*...\* never matches inside **bold**
  const parseInline = (str) => {
    if (!str) return null;
    const parts = [];
    // Order: links > ***bold+italic*** > **bold** > *italic* > `code`
    const regex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|`([^`]+)`/g;
    let last = 0;
    let match;
    while ((match = regex.exec(str)) !== null) {
      if (match.index > last) parts.push(str.slice(last, match.index));
      if (match[1] !== undefined)
        parts.push(
          <a
            key={key++}
            href={match[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="chatbot-link"
          >
            {match[1]}
          </a>
        );
      else if (match[3] !== undefined)
        parts.push(
          <strong key={key++}>
            <em>{match[3]}</em>
          </strong>
        );
      else if (match[4] !== undefined)
        parts.push(<strong key={key++}>{match[4]}</strong>);
      else if (match[5] !== undefined)
        parts.push(<em key={key++}>{match[5]}</em>);
      else if (match[6] !== undefined)
        parts.push(
          <code key={key++} className="chatbot-inline-code">
            {match[6]}
          </code>
        );
      last = match.index + match[0].length;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts;
  };

  const BULLET_RE = /^[ \t]*[-*]\s+(.*)/;
  const ORDERED_RE = /^[ \t]*\d+\.\s+(.*)/;
  const HEADING_RE = /^(#{1,3})\s+(.*)/;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (BULLET_RE.test(line)) {
      // Collect consecutive bullet lines into a <ul>
      const items = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        const m = lines[i].match(BULLET_RE);
        items.push(<li key={key++}>{parseInline(m[1])}</li>);
        i++;
      }
      elements.push(
        <ul key={key++} className="chatbot-md-list">
          {items}
        </ul>
      );
      continue;
    }

    if (ORDERED_RE.test(line)) {
      // Collect consecutive numbered lines into an <ol>
      const items = [];
      while (i < lines.length && ORDERED_RE.test(lines[i])) {
        const m = lines[i].match(ORDERED_RE);
        items.push(<li key={key++}>{parseInline(m[1])}</li>);
        i++;
      }
      elements.push(
        <ol key={key++} className="chatbot-md-list">
          {items}
        </ol>
      );
      continue;
    }

    const headingMatch = line.match(HEADING_RE);
    if (headingMatch) {
      const Tag = `h${Math.min(headingMatch[1].length + 2, 6)}`;
      elements.push(
        <Tag key={key++} className="chatbot-md-heading">
          {parseInline(headingMatch[2])}
        </Tag>
      );
    } else if (line.trim() === "") {
      // Suppress consecutive blank lines - just one gap
      if (elements.length > 0 && elements[elements.length - 1].type !== "br") {
        elements.push(<br key={key++} />);
      }
    } else {
      elements.push(
        <p key={key++} className="chatbot-md-p">
          {parseInline(line)}
        </p>
      );
    }
    i++;
  }
  return elements;
}

const CHAT_API = "/api/chat";

// Strips [GALLERY:project-id] signals (complete or partial) from displayed text.
// Used during streaming so the raw token never flashes to the user.
const GALLERY_STRIP_RE = /\s*\[GALLERY:[a-zA-Z0-9_-]*\]?\s*$/m;

const QUICK_REPLIES = [
  "What are Ghassen's top skills?",
  "Tell me about the Multi-Tenant AI Chatbot Platform",
  "How can I hire Ghassen?",
];

const INITIAL_MESSAGE = {
  role: "model",
  text:
    "Hi! I'm Ghassen's AI assistant. Ask me anything about his background, projects, or experience.",
  id: 0,
};

let msgIdCounter = 1;

const Chatbot = ({ theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false); // typing dots (waiting for first chunk)
  const [isStreaming, setIsStreaming] = useState(false); // tokens flowing in

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const systemPromptRef = useRef(null);

  // Build system prompt once
  if (!systemPromptRef.current) {
    systemPromptRef.current = buildSystemPrompt();
  }

  // Determine theme variant
  const isDarkTheme =
    theme.body &&
    (theme.body.includes("#000") ||
      theme.body.includes("#1") ||
      theme.body.includes("#2") ||
      parseInt(theme.body.replace("#", ""), 16) < 0x888888);

  const triggerVars = {
    "--chatbot-accent": theme.imageHighlight || "#6366f1",
    backgroundColor: theme.imageHighlight || "#6366f1",
    color: "#fff",
    boxShadow: isDarkTheme
      ? `0 4px 24px rgba(0,0,0,0.5)`
      : `0 4px 24px rgba(0,0,0,0.25)`,
  };

  const panelVars = {
    "--chatbot-bg": isDarkTheme
      ? "rgba(18,18,18,0.97)"
      : "rgba(255,255,255,0.97)",
    "--chatbot-accent": theme.imageHighlight || "#6366f1",
    "--chatbot-user-bubble": theme.imageHighlight || "#6366f1",
    "--chatbot-bot-bubble": isDarkTheme ? "#2a2a2a" : "#f0f0f0",
    "--chatbot-text": theme.text,
    "--chatbot-border": isDarkTheme
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.08)",
    "--chatbot-shadow": isDarkTheme
      ? "0 20px 60px rgba(0,0,0,0.6)"
      : "0 20px 60px rgba(0,0,0,0.2)",
    "--chatbot-input-bg": isDarkTheme ? "#1e1e1e" : "#f8f8f8",
    "--chatbot-input-border": isDarkTheme
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)",
    "--chatbot-placeholder": isDarkTheme
      ? "rgba(255,255,255,0.4)"
      : "rgba(0,0,0,0.4)",
  };

  // Auto-scroll on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Auto-focus input on open
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 150);
    }
  }, [isOpen]);

  const streamResponse = useCallback(async (history, newText, botMsgId) => {
    // Normalized request format - provider-agnostic
    const messages = [
      ...history
        .filter((m) => m.role !== "error")
        .map((m) => ({
          role: m.role === "model" ? "assistant" : "user",
          text: m.text,
        })),
      { role: "user", text: newText },
    ];

    const response = await fetch(CHAT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        messages,
        systemPromptFallback: systemPromptRef.current,
        generationConfig: {
          maxOutputTokens: 1024,
          temperature: 0.3,
          topP: 0.8,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error?.message || `HTTP ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let firstChunk = true;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop();

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (!jsonStr || jsonStr === "[DONE]") continue;
        try {
          // Normalized SSE: data: {"text": "..."}
          const data = JSON.parse(jsonStr);
          const chunk = data.text;
          if (!chunk) continue;

          if (firstChunk) {
            firstChunk = false;
            setIsLoading(false);
            setIsStreaming(true);
            setMessages((prev) => [
              ...prev,
              { role: "model", text: chunk, id: botMsgId },
            ]);
          } else {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === botMsgId ? { ...m, text: m.text + chunk } : m
              )
            );
          }
        } catch (_) {}
      }
    }

    // Stream complete - extract and strip the [GALLERY:id] signal if present.
    const GALLERY_SIGNAL_RE = /\s*\[GALLERY:([a-zA-Z0-9_-]+)\]\s*$/m;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== botMsgId) return m;
        const match = m.text.match(GALLERY_SIGNAL_RE);
        if (!match) return m;
        return {
          ...m,
          text: m.text.replace(GALLERY_SIGNAL_RE, "").trimEnd(),
          galleryProjectId: match[1],
        };
      })
    );
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading || isStreaming) return;

      const userMsg = { role: "user", text: trimmed, id: msgIdCounter++ };
      const botMsgId = msgIdCounter++;

      setMessages((prev) => [...prev, userMsg]);
      setInputValue("");
      setIsLoading(true); // show typing dots

      try {
        await streamResponse(messages, trimmed, botMsgId);
      } catch (err) {
        setIsLoading(false);
        setMessages((prev) => [
          ...prev,
          {
            role: "error",
            text: `Something went wrong, sorry about that. Please try again later or reach Ghassen directly:\n\n* 📧 ghassennasser8@gmail.com\n* 📞 +21625196911\n* 💼 [Upwork](https://www.upwork.com/freelancers/~01c478e58397349986?mp_source=share)`,
            id: msgIdCounter++,
          },
        ]);
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
      }
    },
    [isLoading, isStreaming, messages, streamResponse]
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  const showQuickReplies = messages.length === 1;

  return (
    <>
      <button
        className="chatbot-trigger"
        style={triggerVars}
        onClick={toggleOpen}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        title={isOpen ? "Close chat" : "Talk to Ghassen's AI"}
      >
        <span className="chatbot-trigger-icon">{isOpen ? "✕" : "💬"}</span>
        {!isOpen && <span className="chatbot-trigger-label">Ask AI</span>}
      </button>

      {isOpen && (
        <div className="chatbot-panel" style={panelVars}>
          <div
            className="chatbot-header"
            style={{ background: theme.imageHighlight || "#6366f1" }}
          >
            <div className="chatbot-header-left">
              <img src={ghassenImg} alt="Ghassen" className="chatbot-avatar" />
              <div>
                <div className="chatbot-header-name">Talk to Ghassen's AI</div>
                <div className="chatbot-header-status">
                  <span className="chatbot-status-dot" />
                  Online
                </div>
              </div>
            </div>
            <button
              className="chatbot-close-btn"
              onClick={toggleOpen}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, idx) => (
              <React.Fragment key={msg.id}>
                <div
                  className={`chatbot-message chatbot-message--${msg.role}${
                    isStreaming && idx === messages.length - 1
                      ? " chatbot-message--streaming"
                      : ""
                  }`}
                >
                  {msg.role === "model" && (
                    <img
                      src={ghassenImg}
                      alt="Ghassen"
                      className="chatbot-message-avatar"
                    />
                  )}
                  <div className="chatbot-message-bubble">
                    {msg.role === "user"
                      ? msg.text
                      : renderMarkdown(
                          msg.text.replace(GALLERY_STRIP_RE, "").trimEnd()
                        )}
                  </div>
                </div>
                {msg.galleryProjectId && (
                  <ChatbotGalleryStrip
                    projectId={msg.galleryProjectId}
                    theme={theme}
                  />
                )}
              </React.Fragment>
            ))}

            {isLoading && (
              <div className="chatbot-message chatbot-message--model">
                <img
                  src={ghassenImg}
                  alt="Ghassen"
                  className="chatbot-message-avatar"
                />
                <div className="chatbot-typing">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {showQuickReplies && (
            <div className="chatbot-quick-replies">
              {QUICK_REPLIES.map((reply) => (
                <button
                  key={reply}
                  className="chatbot-chip"
                  style={{
                    borderColor: theme.imageHighlight || "#6366f1",
                    color: theme.imageHighlight || "#6366f1",
                  }}
                  onClick={() => sendMessage(reply)}
                  disabled={isLoading || isStreaming}
                >
                  {reply}
                </button>
              ))}
            </div>
          )}

          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              placeholder="Ask about skills, projects, experience…"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading || isStreaming}
            />
            <button
              className="chatbot-send-btn"
              style={{ background: theme.imageHighlight || "#6366f1" }}
              onClick={() => sendMessage(inputValue)}
              disabled={isLoading || isStreaming || !inputValue.trim()}
              aria-label="Send message"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
